import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Tooltip } from 'antd';
import SystemModal from 'components/Shared/Modal';
import { CheckCircleGreenIcon } from 'components/Shared/Icons';
import Can from 'config/Can';
import { ScaVerificationModal, ScaOrderChallengeModal } from 'pages/PaymentsSpa/modals';
import { useAddCardLoad } from 'services/reactQueryHooks/wallet';
import useTranslation from 'utils/hooks/useTranslation';
import useAmplitude from 'utils/hooks/useAmplitude';
import { AMPLITUDE_EVENT_TYPES } from 'constants/amplitude';
import { CARD_LOAD_STEPS, getCardLoadStep } from './helper';

import styles from './style.module.scss';

const openChallengeInNewWindow = url => window.open(url, '_blank', 'noopener,noreferrer');

// Confirms a card top-up: POST card-load-operations, poll the operation, render the SCA/3DS
// challenge when the bank asks for one, and show the done panel once the wallet is credited.
const ConfirmCardPaymentModal = ({
  visible,
  onClose,
  onSuccess,
  walletId,
  amountMinor,
  amountLabel,
  card,
  // The wallet's own currency, for analytics
  currency,
}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  // The OIDC subject is the user account the Wallet Service hands on to JMS as the member
  const userAccountId = useSelector(state => state.user.id);

  const [showVerification, setShowVerification] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);

  const { mutate, isSubmitting, isPolling, isSuccess, isError, sca, reset } = useAddCardLoad({
    currency,
    onReset: () => {
      setShowVerification(false);
      setShowChallenge(false);
    },
  });
  const step = getCardLoadStep({ isSubmitting, isPolling, sca, isSuccess });

  useEffect(() => {
    if (sca) {
      setShowVerification(true);
    } else {
      // The challenge is cleared once spent — take the modals down with it and keep polling
      setShowVerification(false);
      setShowChallenge(false);
    }
  }, [sca]);

  useEffect(() => {
    if (isSuccess || isError) {
      setShowVerification(false);
      setShowChallenge(false);
    }
  }, [isSuccess, isError]);

  const handleConfirm = () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCardLoadSubmitButtonClicked);
    mutate({ walletId, amountMinor, paymentAccountId: card?.id, userAccountId });
  };

  const handleContinueToChallenge = () => {
    setShowVerification(false);
    setShowChallenge(true);
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletCardLoadScaChallengeModalViewed);
  };

  // Closing at any point abandons the attempt (stops polling); the backend finishes on its own.
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleDone = () => {
    if (onSuccess) {
      onSuccess();
    }
    handleClose();
  };

  const isInProgress = step !== CARD_LOAD_STEPS.confirm && step !== CARD_LOAD_STEPS.done;

  const scaFallback = sca?.redirectUrl ? (
    <div className={styles.scaFallback} data-testid="confirmCardPaymentModalScaFallback">
      <span>
        {translateText(
          'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.scaOpenInNewWindowHint',
        )}
      </span>
      <Button
        type="link"
        onClick={() => openChallengeInNewWindow(sca.redirectUrl)}
        data-testid="confirmCardPaymentModalScaOpenInNewWindow"
      >
        {translateText(
          'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.scaOpenInNewWindow',
        )}
      </Button>
    </div>
  ) : null;

  return (
    <SystemModal
      title={
        step === CARD_LOAD_STEPS.done ? (
          ''
        ) : (
          <span className={styles.dashboardModalTitle}>
            {translateText('corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.title')}
          </span>
        )
      }
      titleLevel={5}
      width={440}
      visible={visible}
      onCancel={handleClose}
      footer={null}
      bodyStyle={{ padding: '20px' }}
      className={styles.dashboardModal}
      destroyOnClose
      centered
      data-testid="confirmCardPaymentModal"
    >
      <ScaVerificationModal
        visible={showVerification}
        onOk={handleContinueToChallenge}
        onCancel={handleClose}
      />
      {showChallenge && (
        <ScaOrderChallengeModal
          visible={showChallenge}
          challengeHtml={sca?.challengeHtml}
          challengeUrl={sca?.challengeHtml ? null : sca?.redirectUrl}
          onCancel={handleClose}
          footerContent={scaFallback}
        />
      )}

      {step === CARD_LOAD_STEPS.done ? (
        <div className={styles.closeDone} data-testid="confirmCardPaymentModalDone">
          <span className={styles.closeDoneIcon}>
            <CheckCircleGreenIcon />
          </span>
          <div className={styles.closeDoneTitle}>
            {translateText(
              'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.doneTitle',
              { amount: amountLabel },
            )}
          </div>
          <div className={styles.closeDoneDescription}>
            {translateText(
              'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.doneDescription',
            )}
          </div>
          <Button
            type="primary"
            onClick={handleDone}
            data-testid="confirmCardPaymentModalDoneButton"
          >
            {translateText('corporateAccounts.actions.done')}
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.confirmCardCopy}>
            {translateText('corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.copy')}
          </div>

          <div className={styles.confirmCardSummary}>
            <div className={styles.confirmCardSummaryRow}>
              <span className={styles.confirmCardSummaryLabel}>
                {translateText(
                  'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.amount',
                )}
              </span>
              <span
                className={styles.confirmCardSummaryValue}
                data-testid="confirmCardPaymentModalAmount"
              >
                {amountLabel}
              </span>
            </div>
            <div className={styles.confirmCardSummaryRow}>
              <span className={styles.confirmCardSummaryLabel}>
                {translateText('corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.card')}
              </span>
              <span
                className={styles.confirmCardSummaryValue}
                data-testid="confirmCardPaymentModalCard"
              >
                {card?.label ?? ''}
              </span>
            </div>
          </div>

          {/* Only fleet managers may charge the card (mirrors the Wallet Service role matrix) */}
          <Can I="confirm" a="Wallet" passThrough>
            {allowed => (
              <Tooltip
                title={
                  allowed
                    ? null
                    : translateText(
                        'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.confirmDisabled',
                      )
                }
              >
                <span>
                  <Button
                    type="primary"
                    block
                    loading={isInProgress}
                    disabled={!allowed || isInProgress || !card?.id}
                    className={styles.confirmCardSubmit}
                    onClick={handleConfirm}
                    data-testid="confirmCardPaymentModalSubmit"
                  >
                    {translateText(
                      'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.confirm',
                    )}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>

          {(step === CARD_LOAD_STEPS.processing || step === CARD_LOAD_STEPS.sca) && (
            <div className={styles.confirmCardStatus} data-testid="confirmCardPaymentModalStatus">
              {translateText(
                'corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.processing',
              )}
            </div>
          )}
        </>
      )}
    </SystemModal>
  );
};

export default ConfirmCardPaymentModal;
